import { Component, Input, OnInit, Self, SkipSelf } from '@angular/core';
import { ControlContainer } from '@angular/forms';
import { DepreciationMethodId } from '@assetaccountant/api/models/depreciation-method-id';
import { DepreciationRuleId } from '@assetaccountant/api/models/depreciation-rule-id';
import { DepreciationRuleDto } from '@assetaccountant/api/models/depreciation-rule.dto';
import { RegisterPermissionType } from '@assetaccountant/api/models/register-permission-type';
import { RegisterDto } from '@assetaccountant/api/models/register.dto';
import { DepreciationApi } from '@assetaccountant/api/services/depreciation.api';
import { DepreciationMethodKey } from '@assetaccountant/core/models/depreciation-method';
import {
  getRuleName,
  ruleIsValidForMethod,
  ruleIsValidForPool,
} from '@assetaccountant/core/models/depreciation-rule';
import { toUniversalDateString } from '@assetaccountant/core/utils/date-utils';
import {
  TypedFormArray,
  TypedFormBuilder,
  TypedFormControl,
  TypedFormGroup,
} from '@assetaccountant/core/utils/typed-forms';
import moment from 'moment';

interface RuleFormData {
  depreciationMethodId?: DepreciationMethodId | DepreciationMethodKey;
  poolCode?: string | null;
  depreciationRules?: Array<{ id: DepreciationRuleId; group?: string | null }>;
}

@Component({
  selector: 'app-depreciation-rule-selector',
  templateUrl: './depreciation-rule-selector.component.html',
  styleUrl: './depreciation-rule-selector.component.scss',
  standalone: false,
})
export class DepreciationRuleSelectorComponent implements OnInit {
  readonly RegisterPermissionType = RegisterPermissionType;

  @Input() register: RegisterDto;
  @Input() regimeId: number;
  @Input() acquisitionDate?: string | null;
  @Input() firstUseDate?: string | null;

  get formGroup() {
    return this.formGroupContainer.control as TypedFormGroup<{
      depreciationMethodId?: TypedFormControl<
        DepreciationMethodId | DepreciationMethodKey | null | undefined
      >;
      poolCode?: TypedFormControl<string | null | undefined>;
      depreciationRules: TypedFormArray<
        TypedFormControl<{ id: DepreciationRuleId; group?: string | null }>
      >;
    }>;
  }
  get formArray() {
    return this.formArrayContainer.control as TypedFormArray<
      TypedFormControl<{ id: DepreciationRuleId; group?: string | null }>
    >;
  }

  regimeDepreciationRules: DepreciationRuleDto[];

  constructor(
    private fb: TypedFormBuilder,
    private depreciationApi: DepreciationApi,
    @SkipSelf() private formGroupContainer: ControlContainer,
    @Self() private formArrayContainer: ControlContainer,
  ) {}

  ngOnInit() {
    this.depreciationApi.get().subscribe(regimeMethods => {
      this.regimeDepreciationRules =
        regimeMethods.find(r => r.regimeId === this.regimeId)?.depreciationRules ?? [];

      if (this.formGroup) {
        if (this.formGroup.controls.depreciationMethodId && !this.formGroup.controls.poolCode) {
          this.regimeDepreciationRules = this.regimeDepreciationRules.filter(r =>
            ruleIsValidForMethod(r.id),
          );
        } else if (
          this.formGroup.controls.poolCode &&
          !this.formGroup.controls.depreciationMethodId
        ) {
          this.regimeDepreciationRules = this.regimeDepreciationRules.filter(r =>
            ruleIsValidForPool(r.id),
          );
        }
      }

      if (this.formArray && this.formArray.length === 0) {
        this.regimeDepreciationRules.forEach(r =>
          this.formArray.push(
            this.fb.control({ value: { id: r.id, group: r.group }, disabled: true }),
          ),
        );
      }
    });
  }

  isValidRule(rule: DepreciationRuleDto, formValue: RuleFormData) {
    return (
      this.isValidRuleForDate(rule) &&
      this.isValidRuleForPoolOrMethod(rule, formValue) &&
      this.isValidRuleForRuleGroup(rule, formValue.depreciationRules)
    );
  }

  isValidRuleForDate(rule: DepreciationRuleDto) {
    const acquisitionDate =
      this.acquisitionDate || toUniversalDateString(moment(), { throwOnFailure: true });
    const firstUseDate =
      this.firstUseDate || toUniversalDateString(moment(), { throwOnFailure: true });

    return (
      rule.acquiredFrom <= acquisitionDate &&
      acquisitionDate < rule.acquiredTo &&
      rule.firstUseFrom <= firstUseDate &&
      firstUseDate < rule.firstUseTo
    );
  }

  isValidRuleForPoolOrMethod(
    rule: DepreciationRuleDto,
    { poolCode, depreciationMethodId }: RuleFormData,
  ) {
    if (poolCode) {
      if (!ruleIsValidForPool(rule.id, poolCode)) {
        return false;
      }
    } else if (depreciationMethodId) {
      if (!ruleIsValidForMethod(rule.id, depreciationMethodId)) {
        return false;
      }
    }

    return true;
  }

  isValidRuleForRuleGroup(
    rule: DepreciationRuleDto,
    depreciationRules?: Array<{ id: number; group?: string | null }>,
  ) {
    if (rule.group && depreciationRules?.some(r => r.id !== rule.id && r.group === rule.group)) {
      return false;
    }
    return true;
  }

  getRuleName(rule: DepreciationRuleDto, formValue: RuleFormData) {
    if (!this.register) {
      return rule.name;
    }

    const firstUseDate =
      this.firstUseDate || toUniversalDateString(moment(), { throwOnFailure: true });

    return getRuleName(rule, firstUseDate, formValue, {
      referenceStartDate: this.register.reportingYearStartDate,
    });
  }

  getRuleLabel(rule: DepreciationRuleDto, formValue: RuleFormData) {
    if (rule.label) {
      return rule.label;
    }
    return 'Apply ' + this.getRuleName(rule, formValue);
  }
}
